import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, FileText, Link2, Mic, MicOff, Send, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, messageErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChoixListe } from "./ChoixListe";
import { useDictee } from "@/components/assistant/useDictee";

const PROFILS = [["dirigeant", "Dirigeant"], ["responsable", "Responsable plateau"], ["agent", "Agents"], ["confirmateur", "Confirmateur"]];
const heure = (d) => new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const Bulle = ({ m, onChoix, actif }) => {
  const moi = m.sens === "entrant";
  return (
    <div className={`flex ${moi ? "justify-end" : "justify-start"}`} data-testid={`wa-message-${m.sens}`}>
      <div className="max-w-[85%] px-3 py-2 text-sm space-y-1.5 break-words" style={{ background: moi ? "color-mix(in srgb, var(--success) 22%, var(--surface))" : "var(--surface)", borderRadius: 14 }}>
        {m.vocal && <p className="text-xs font-semibold flex items-center gap-1"><Mic size={12} />Message vocal retranscrit</p>}
        <p>{m.texte}</p>
        {m.lien && <Link to={m.lien} className="text-xs font-semibold underline flex items-center gap-1 min-h-[32px]"><Link2 size={12} />Ouvrir la fiche</Link>}
        {(m.pieces || []).map((p) => <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} className="text-xs font-semibold flex items-center gap-1 min-h-[32px]" data-testid="wa-attachment"><FileText size={12} />{p.nom}</a>)}
        {m.boutons && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button type="button" disabled={!actif} onClick={() => onChoix(m.action_id, "valider")} className="at-btn at-btn-primary !min-h-[44px]" data-testid="wa-button-validate"><Check size={15} />Valider</button>
            <button type="button" disabled={!actif} onClick={() => onChoix(m.action_id, "annuler")} className="at-btn at-btn-ghost !min-h-[44px]" data-testid="wa-button-cancel"><X size={15} />Annuler</button>
          </div>
        )}
        <p className="text-[10px] at-muted text-right">{heure(m.date)}</p>
      </div>
    </div>
  );
};

const Reglages = ({ r, onMaj }) => {
  const basculer = (cle, liste, v) => onMaj({ [cle]: liste.includes(v) ? liste.filter((x) => x !== v) : [...liste, v] });
  return (
    <section className="at-card p-5 space-y-3" data-testid="wa-settings">
      <p className="font-bold">Assistant WhatsApp</p>
      <p className="text-xs at-muted">Seuls les numéros enregistrés sur un utilisateur (Paramètres › Utilisateurs) sont reconnus. Les partenaires n'y ont jamais accès.</p>
      {PROFILS.map(([code, label]) => (
        <label key={code} className="at-soft min-h-[44px] px-3 flex items-center gap-3">
          <input type="checkbox" className="w-5 h-5" disabled={code === "dirigeant"} checked={r.profils.includes(code)} onChange={() => basculer("profils", r.profils, code)} style={{ accentColor: "var(--accent)" }} data-testid={`wa-profile-${code}`} />{label}
        </label>
      ))}
      <p className="font-semibold text-sm pt-2">Champs sensibles (envoyés seulement au Dirigeant et au Confirmateur)</p>
      {Object.entries(r.catalogue || {}).map(([code, label]) => (
        <label key={code} className="at-soft min-h-[44px] px-3 flex items-center gap-3">
          <input type="checkbox" className="w-5 h-5" checked={r.sensibles.includes(code)} onChange={() => basculer("sensibles", r.sensibles, code)} style={{ accentColor: "var(--accent)" }} data-testid={`wa-sensitive-${code}`} />{label}
        </label>
      ))}
    </section>
  );
};

export const TestWhatsApp = () => {
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [numero, setNumero] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [texte, setTexte] = useState("");
  const fin = useRef(null);
  const charger = useCallback(() => api.get("/whatsapp/numeros").then((x) => { setR(x.data); setNumero((n) => n || x.data.numeros[0]?.telephone || ""); }), []);
  const conv = useCallback(() => numero && api.get("/whatsapp/conversation", { params: { numero } }).then((x) => setMsgs(x.data)), [numero]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { conv(); }, [conv]);
  useEffect(() => { fin.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  const envoyer = async (corps) => {
    try { await api.post("/whatsapp/simuler", { numero, ...corps }); conv(); } catch (e) { toast.error(messageErreur(e)); }
  };
  const envoyerRef = useRef(envoyer);
  envoyerRef.current = envoyer;
  const { actif, interim, demarrer, arreter } = useDictee((t) => envoyerRef.current({ texte: t, vocal: true }));
  const micro = () => (actif ? arreter() : demarrer() || toast.error("Dictée indisponible : écrivez le message, il partira comme texte."));
  const sortant = async (type) => { try { await api.post("/whatsapp/sortant", { numero, type }); conv(); } catch (e) { toast.error(messageErreur(e)); } };
  const majReglages = async (corps) => { try { await api.put("/whatsapp/reglages", corps); charger(); } catch (e) { toast.error(messageErreur(e)); } };
  if (!r) return null;
  const derniere = [...msgs].reverse().find((m) => m.boutons);
  return (
    <div className="space-y-4">
      {user.role === "dirigeant" && <Reglages r={r} onMaj={majReglages} />}
      <section className="at-card p-4 space-y-3 min-w-0" data-testid="wa-test">
        <p className="font-bold">Test WhatsApp <span className="text-xs at-muted font-normal">(simulation)</span></p>
        <ChoixListe valeur={numero} onChange={setNumero} options={[...r.numeros.map((n) => ({ code: n.telephone, label: `${n.nom} · ${n.telephone}` })), { code: "0699999999", label: "Numéro inconnu · 0699999999" }]} testId="wa-number-select" />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => sortant("briefing")} className="at-btn at-btn-ghost text-sm" data-testid="wa-send-briefing">Briefing du matin</button>
          <button type="button" onClick={() => sortant("rapport")} className="at-btn at-btn-ghost text-sm" data-testid="wa-send-report">Rapport du soir</button>
        </div>
        <div className="p-3 space-y-2 max-h-[55vh] overflow-y-auto" style={{ background: "color-mix(in srgb, var(--success) 8%, var(--bg))", borderRadius: 16 }} data-testid="wa-conversation">
          {msgs.length === 0 && <p className="text-sm at-muted text-center">Écrivez par exemple « Où en est le dossier de la mairie de Plabennec ? »</p>}
          {msgs.map((m) => <Bulle key={m.id} m={m} actif={m === derniere} onChoix={(id, choix) => envoyer({ action_id: id, choix })} />)}
          {actif && <p className="text-sm italic at-muted text-right" data-testid="wa-live-transcript">{interim || "J'écoute…"}</p>}
          <div ref={fin} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (texte.trim()) { envoyer({ texte }); setTexte(""); } }} className="flex gap-2">
          <button type="button" onClick={micro} aria-label="Message vocal" className="at-micro w-12 h-12 shrink-0 flex items-center justify-center" data-testid="wa-voice">{actif ? <MicOff size={18} /> : <Mic size={18} />}</button>
          <input value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Message" className="at-input" data-testid="wa-input" />
          <button type="submit" disabled={!texte.trim()} aria-label="Envoyer" className="at-icon-btn at-btn-primary w-12 h-12 shrink-0" data-testid="wa-send"><Send size={17} /></button>
        </form>
      </section>
    </div>
  );
};
